import { useTranslation } from 'react-i18next';
import type { ReactNode } from 'react';

import StyledError from '/shared/styled/StyledError';

interface ErrorLike {
  message?: string;
  code?: string;
  response?: {
    data?:
      | {
          message: string;
        }
      | string;
  };
}

export type DisplayError = ErrorLike | globalThis.Error | object | string | null | undefined;

interface Props {
  error?: DisplayError;
  $position?: string;
  children?: ReactNode;
}

function isErrorLike(error: DisplayError): error is ErrorLike {
  return error != null && typeof error === 'object';
}

function Error({ error = null, $position = 'absolute', children = null }: Props) {
  const { i18n, t } = useTranslation();
  let message = 'Unknown error';
  if (error) {
    if (isErrorLike(error) && error.response?.data) {
      if (typeof error.response.data === 'string') {
        message = error.response.data;
      } else {
        message = error.response.data.message;
      }
    } else if (typeof error === 'string') {
      message = error;
    } else if (error instanceof globalThis.Error) {
      message = error.message;
    } else if (isErrorLike(error) && error.message) {
      message = error.message;
    } else {
      message = 'Could not connect to the server. Please try again.';
    }
    if (isErrorLike(error) && error.code && i18n.exists(`shared:errors.${error.code}`)) {
      message = t(`shared:errors.${error.code}`, message);
    }
  } else if (children) {
    return (
      <StyledError $position={$position} aria-live="assertive" aria-relevant="additions removals" role="alert">
        {children}
      </StyledError>
    );
  } else {
    message = 'Unknown error';
  }
  return (
    <StyledError $position={$position} aria-live="assertive" aria-relevant="additions removals" role="alert">
      {message}
    </StyledError>
  );
}

export default Error;
